import { useState } from "react";
import { Button } from "@/core/components/ui/button";
import { Stack } from "@/core/components/ui/stack";
import {
  TimeRangePicker,
  type TimeRange,
} from "@/core/components/ui/time-range-picker";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { REPORT_PERIOD_PRESETS } from "./constants";
import { useCreateReport } from "./query";
import type { ReportDates } from "./types";
import { dateRangeError, reportError, toIsoDate } from "./utils";

const toDates = (range: TimeRange | null): ReportDates | null =>
  range?.from && range.to
    ? { start_date: toIsoDate(range.from), end_date: toIsoDate(range.to) }
    : null;

/**
 * The page header's right side: pick a period, run it. Period context first and
 * then the action on it, the same order as the payroll header, so the one thing
 * this page creates lives where every other page keeps its primary action.
 */
export const NewReportControls = ({
  businessId,
  onCreated,
}: {
  businessId: string;
  onCreated: (runId: string) => void;
}) => {
  const [range, setRange] = useState<TimeRange | null>(null);
  const create = useCreateReport(businessId);
  const dates = toDates(range);
  const validation = dates ? dateRangeError(dates) : null;

  return (
    <Stack direction="column" align="end" gap="xs" className="w-full lg:w-auto">
      <Stack
        direction="row"
        align="center"
        justify="end"
        gap="sm"
        className="w-full flex-wrap gap-y-2 lg:w-auto"
      >
        <TimeRangePicker
          placeholder="Select a report period"
          presets={REPORT_PERIOD_PRESETS}
          value={range}
          onChange={setRange}
          // A profitability report is worth running for any period, including
          // one no preset covers. Without typing, reaching an old quarter means
          // clicking the calendar back a month at a time.
          allowTextEntry
        />
        <Button
          size="sm"
          disabled={!dates || !!validation}
          loading={create.isPending}
          onClick={() => {
            if (!dates || validation) return;
            create.mutate(dates, {
              onSuccess: (run) => onCreated(run.id),
              // A failure here is about the request, not the page, so it does
              // not get to push the history down.
              onError: (error) =>
                toast.error("The report could not be started", {
                  description: reportError(error),
                }),
            });
          }}
        >
          Run report
        </Button>
      </Stack>
      {/* A disabled button with no reason beside it is just a dead control. */}
      {validation && (
        <Typography variant="bodyW2" className="text-destructive">
          {validation}
        </Typography>
      )}
    </Stack>
  );
};
